import { useEffect, useState } from "react";
import api, { extractErr } from "../lib/api";
import { UserPlus, X, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const load = () => api.get("/team").then(r => setTeam(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (u.id === user?.id) { toast.error("Cannot remove yourself"); return; }
    if (!confirm(`Remove ${u.name}?`)) return;
    try { await api.delete(`/team/${u.id}`); toast.success(`Removed ${u.name}`); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">Workspace</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Team</h1>
          <p className="text-slate-500 mt-1 text-sm">{team.length} 位团队成员可以登入这个 EduRISE workspace</p>
        </div>
        <button
          data-testid="invite-btn"
          onClick={() => setShowInvite(true)}
          className="px-4 py-2 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700 flex items-center gap-1.5"
        >
          <UserPlus size={15} /> Invite member
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {team.map(u => (
          <div key={u.id} data-testid={`team-${u.id}`} className="bg-white border border-slate-200 rounded-sm p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-700 text-white flex items-center justify-center font-bold">
              {(u.name || "?").split(" ").slice(0,2).map(s => s[0]?.toUpperCase()).join("")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{u.name}</div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{u.role}</div>
            </div>
            {u.id !== user?.id && (
              <button
                data-testid={`remove-${u.id}`}
                onClick={() => remove(u)}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                title="Remove member"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        ))}
      </div>
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function InviteModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setResult(data);
      toast.success("Invited!");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const copyPw = () => {
    navigator.clipboard.writeText(`Email: ${result.email}\nPassword: ${result.temp_password}`);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-md shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display text-lg font-bold">{result ? "邀请成功 · Invited" : "邀请团队成员"}</div>
          <button onClick={result ? onDone : onClose}><X size={18} /></button>
        </div>
        {result ? (
          <div className="p-5 space-y-3">
            <div className="text-sm text-slate-600">已创建账号,请把下面资料<b>安全</b>转告 {result.name}:</div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm font-mono text-sm space-y-1">
              <div>Email: <span className="text-emerald-800">{result.email}</span></div>
              <div>Password: <span className="text-orange-700" data-testid="invite-temp-password">{result.temp_password}</span></div>
            </div>
            <button
              data-testid="copy-invite"
              onClick={copyPw}
              className="w-full py-2 bg-slate-950 text-white text-xs font-semibold rounded-sm hover:bg-emerald-700 flex items-center justify-center gap-1.5"
            >
              <Copy size={12} /> Copy credentials
            </button>
            <button
              onClick={onDone}
              className="w-full py-2 border border-slate-200 text-sm font-semibold rounded-sm hover:bg-slate-50"
            >Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Full name" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
            <input data-testid="invite-email" required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="email@edurise.com" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
            <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
              <option value="member">Member (member)</option>
              <option value="owner">Owner (owner)</option>
            </select>
            <button data-testid="invite-submit" disabled={busy} type="submit" className="w-full py-2.5 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-50">
              {busy ? "邀请中…" : "创建并生成密码"}
            </button>
            <div className="text-[10px] text-slate-500">系统会生成一次性随机密码,登入后可让新成员自行改密</div>
          </form>
        )}
      </div>
    </div>
  );
}
